import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer, scaleIn } from '@/lib/animations';
import { Link } from 'react-router-dom';
import { Settings, Brain, BarChart3, PhoneCall, Timer, TrendingUp, Smile, ArrowRight } from 'lucide-react';
import { InfoTooltip } from './Util';

const STEPS = [
  {
    number: '01',
    icon: Settings,
    title: 'We Build & Train Your AI',
    desc: 'We design a custom AI agent trained on your exact data — your products, FAQs, workflows, and tone of voice. No generic prompts, no off-the-shelf bots.',
  },
  {
    number: '02',
    icon: Brain,
    title: 'AI Handles the Volume',
    desc: 'Your AI goes live across calls and webchat, handling enquiries 24/7, qualifying leads, booking appointments, and escalating to your team only when it truly matters.',
  },
  {
    number: '03',
    icon: BarChart3,
    title: 'You See Everything',
    desc: 'A single dashboard gives you full visibility — transcripts, performance metrics, and outcomes. We manage, monitor, and continuously improve your AI so you never have to.',
  },
];

function HeroSection() {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-10">
      <motion.p variants={fadeInUp} className="motion-label">HOW IT WORKS</motion.p>
      <motion.h2 variants={fadeInUp} className="motion-heading">
        Up and Running in <span className="text-brand-blue">Three Steps</span>
      </motion.h2>
      <motion.p variants={fadeInUp} className="motion-text">
        From kickoff call to live AI agent — we handle everything. No technical team needed on your side.
      </motion.p>
    </motion.div>
  );
}

export function ToolTip() {
  return (
    <InfoTooltip label="What performance metrics does the dashboard track?" title="Performance Metrics, Explained" side="left">
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <PhoneCall size={13} className="text-brand-blue-light mt-0.5 flex-shrink-0" />
          <span><strong className="text-brand-off-white">Call & chat volume</strong> — total enquiries your AI handled across voice and webchat.</span>
        </li>
        <li className="flex items-start gap-2">
          <Timer size={13} className="text-brand-blue-light mt-0.5 flex-shrink-0" />
          <span><strong className="text-brand-off-white">Average handle time</strong> — how quickly each enquiry is resolved, start to finish.</span>
        </li>
        <li className="flex items-start gap-2">
          <TrendingUp size={13} className="text-brand-blue-light mt-0.5 flex-shrink-0" />
          <span><strong className="text-brand-off-white">Resolution & conversion rate</strong> — enquiries closed without escalation, and leads booked or qualified.</span>
        </li>
        <li className="flex items-start gap-2">
          <Smile size={13} className="text-brand-blue-light mt-0.5 flex-shrink-0" />
          <span><strong className="text-brand-off-white">Customer satisfaction</strong> — sentiment and outcome tracking across every conversation.</span>
        </li>
      </ul>
    </InfoTooltip>
  )
}

export function HowItWorksDesktop() {
  return (
    <section data-testid="how-it-works-section" className="py-24 md:py-32 bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <HeroSection />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
          {STEPS.map((step, idx) => (
            <motion.div
              key={step.number}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-50px' }}
              variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: idx * 0.15, duration: 0.55, ease: [0.22, 1, 0.36, 1] } } }}
              className="relative flex flex-col"
            >

              {/* Icon + step number */}
              <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center mb-6 flex-shrink-0"
                style={{ background: 'rgba(var(--c-purple-rgb),0.12)', border: '1px solid rgba(var(--c-purple-rgb),0.28)' }}>
                <step.icon size={28} className="text-brand-blue" />
                <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ background: 'var(--c-purple-600)' }}>
                  {step.number.replace('0', '')}
                </span>
              </div>

              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={scaleIn}
          className="glass-card p-2 md:p-3 mt-16 max-w-4xl mx-auto shadow-[0_0_60px_rgba(var(--c-purple-rgb),0.15)] relative"
        >
          <div className="tool-tip-location">
            <ToolTip />
          </div>
          <img
            src="/img/dashboard/overview.webp"
            alt="Custum AI mobile dashboard overview"
            loading="lazy"
            className="w-full h-auto rounded-xl"
          />
        </motion.div>

        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}
          className="mt-8 text-center"
        >
          <Link to="/contact/" className="cta-button">
            Get Started Today <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export function HowItWorksMobile() {
  return (
    <section data-testid="how-it-works-section" className="py-24 md:py-32 bg-brand-charcoal">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <HeroSection />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={scaleIn}
            className="glass-card p-2 md:p-3 mt-16 max-w-4xl mx-auto shadow-[0_0_60px_rgba(var(--c-purple-rgb),0.15)] relative"
          >
            <div className="tool-tip-location">
              <ToolTip />
            </div>
            <img
              src="/img/dashboard/mobile_dashboard.webp"
              alt="Custum AI mobile dashboard overview"
              loading="lazy"
              className="w-full h-auto rounded-xl"
            />
          </motion.div>

          <div className="flex flex-col gap-12">
            {STEPS.map((step, idx) => (
              <motion.div
                key={step.number}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={{ hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: idx * 0.15, duration: 0.55, ease: [0.22, 1, 0.36, 1] } } }}
                className="relative flex items-start gap-6"
              >
                {/* Icon + step number */}
                <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center mb-6 flex-shrink-0"
                  style={{ background: 'rgba(var(--c-purple-rgb),0.12)', border: '1px solid rgba(var(--c-purple-rgb),0.28)' }}>
                  <step.icon size={28} className="text-brand-blue" />
                  <span className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                    style={{ background: 'var(--c-purple-600)' }}>
                    {step.number.replace('0', '')}
                  </span>
                </div>

                <div>
                    <h3 className="text-xl font-bold text-brand-white mb-3">{step.title}</h3>
                  <p className="text-sm text-brand-muted leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}